import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Chip,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon,
  Forum as ForumIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { apiClient } from '../../../framework/api/api.client';

interface Thread {
  id: string;
  threadId: string;
  userId: string;
  title: string;
  status: string;
  messageCount: number;
  lastMessageAt: Date;
  conversationContext?: any;
  createdAt: Date;
}

interface Message {
  id: string;
  messageId: string;
  threadId: string;
  userId: string;
  role: string;
  content: any[];
  createdAt: Date;
}

export const HistoryPanel: React.FC = () => {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [expandedThread, setExpandedThread] = useState<string | null>(null);
  const [threadMessages, setThreadMessages] = useState<Record<string, Message[]>>({});
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [userIdFilter, setUserIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRangeFilter, setDateRangeFilter] = useState('7d');
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    loadThreads();
  }, []);

  const loadThreads = async () => {
    setLoading(true);
    try {
      let response;
      
      // If search text provided, use content search endpoint
      if (searchText.trim()) {
        const params: any = { searchText: searchText.trim(), limit: 50 };
        if (userIdFilter) params.userId = userIdFilter;
        
        const queryString = new URLSearchParams(params).toString();
        response = await apiClient.call(`/ai-observatory/threads/search?${queryString}`);
      } else {
        // Otherwise use regular thread list with filters
        const filters: any = { limit: 50 };
        
        if (userIdFilter) filters.userId = userIdFilter;
        if (statusFilter !== 'all') filters.status = statusFilter;
        
        // Date range filter
        if (dateRangeFilter !== 'all') {
          const now = new Date();
          const startDate = new Date();
          
          switch (dateRangeFilter) {
            case '1d':
              startDate.setDate(now.getDate() - 1);
              break;
            case '7d':
              startDate.setDate(now.getDate() - 7);
              break;
            case '30d':
              startDate.setDate(now.getDate() - 30);
              break;
          }
          
          filters.startDate = startDate.toISOString();
        }
        
        const queryString = new URLSearchParams(filters).toString();
        response = await apiClient.call(`/ai-observatory/threads?${queryString}`);
      }
      
      setThreads(response.threads || []);
    } catch (error) {
      console.error('Error loading threads:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadThreadMessages = async (threadId: string) => {
    if (!threadId) {
      console.error('❌ Cannot load messages - threadId is undefined');
      return;
    }
    
    if (threadMessages[threadId]) {
      // Already loaded
      return;
    }
    
    try {
      console.log(`🔍 Loading messages for thread: ${threadId}`);
      const response = await apiClient.call(`/ai-observatory/threads/${threadId}`);
      setThreadMessages(prev => ({
        ...prev,
        [threadId]: response.messages || [],
      }));
    } catch (error) {
      console.error('Error loading thread messages:', error);
    }
  };

  const toggleThread = async (threadId: string) => {
    if (!threadId) {
      console.error('❌ Cannot toggle thread - threadId is undefined');
      return;
    }
    
    if (expandedThread === threadId) {
      setExpandedThread(null);
    } else {
      setExpandedThread(threadId);
      await loadThreadMessages(threadId);
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString();
  };

  const getRoleColor = (role: string): 'primary' | 'secondary' | 'default' => {
    if (role === 'user') return 'primary';
    if (role === 'assistant') return 'secondary';
    return 'default';
  };

  return (
    <Card>
      <CardHeader
        title={<><HistoryIcon /> Conversation History</>}
        action={
          <IconButton onClick={loadThreads} size="small">
            <RefreshIcon />
          </IconButton>
        }
      />
      <CardContent>
        {/* Search & Filters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Text Search (Full Width) */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Search Message Content"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && loadThreads()}
              placeholder="Search conversations..."
            />
          </Grid>
          
          {/* Filters */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="User ID"
              value={userIdFilter}
              onChange={(e) => setUserIdFilter(e.target.value)}
              placeholder="Filter by user..."
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
                disabled={!!searchText.trim()} // Disable when searching content
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Time Range</InputLabel>
              <Select
                value={dateRangeFilter}
                onChange={(e) => setDateRangeFilter(e.target.value)}
                label="Time Range"
                disabled={!!searchText.trim()} // Disable when searching content
              >
                <MenuItem value="1d">Last 24 Hours</MenuItem>
                <MenuItem value="7d">Last 7 Days</MenuItem>
                <MenuItem value="30d">Last 30 Days</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={loadThreads}
              disabled={loading}
              startIcon={<SearchIcon />}
            >
              Search
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {/* Results */}
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography>Loading threads...</Typography>
          </Box>
        ) : threads.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography color="text.secondary">
              No threads found. Try adjusting your filters.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Found {threads.length} conversation{threads.length !== 1 ? 's' : ''}
                {searchText.trim() && (
                  <Chip 
                    label={`Matching: "${searchText.trim()}"`} 
                    size="small" 
                    sx={{ ml: 1 }}
                    onDelete={() => {
                      setSearchText('');
                      loadThreads();
                    }}
                  />
                )}
              </Typography>
              {searchText.trim() && (
                <Typography variant="caption" color="text.secondary">
                  Results ranked by relevance
                </Typography>
              )}
            </Box>
            
            <List sx={{ maxHeight: 600, overflow: 'auto' }}>
              {threads.map((thread) => {
                // Debug logging for thread structure
                if (!thread.threadId) {
                  console.warn('⚠️ Thread missing threadId:', thread);
                }
                
                return (
                <React.Fragment key={thread.threadId || thread.id}>
                  <ListItem
                    button
                    onClick={() => toggleThread(thread.threadId)}
                    sx={{
                      bgcolor: 'background.default',
                      mb: 1,
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ForumIcon />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {thread.title}
                          </Typography>
                          <Badge badgeContent={thread.messageCount} color="primary" sx={{ ml: 1 }} />
                          <Chip label={thread.status} size="small" color="success" />
                          <Box sx={{ flex: 1 }} />
                          {expandedThread === thread.threadId ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          {/* Show matched content preview if this is a search result */}
                          {(thread as any).firstMatch && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                bgcolor: 'warning.50', 
                                p: 1, 
                                borderRadius: 1, 
                                mb: 1,
                                fontStyle: 'italic',
                              }}
                            >
                              "...{(thread as any).firstMatch}..."
                              {(thread as any).matchedMessageCount > 1 && (
                                <Chip 
                                  label={`+${(thread as any).matchedMessageCount - 1} more matches`} 
                                  size="small" 
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            <PersonIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                            User: {thread.userId?.substring(0, 12)}...
                            {' • '}
                            <AccessTimeIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                            Last: {formatDate(thread.lastMessageAt)}
                            {thread.conversationContext?.applicationKey && (
                              <> • App: {thread.conversationContext.applicationKey}</>
                            )}
                            {thread.conversationContext?.featureKey && (
                              <> • Feature: {thread.conversationContext.featureKey}</>
                            )}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  
                  <Collapse in={expandedThread === thread.threadId} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
                      {threadMessages[thread.threadId] ? (
                        <List>
                          {threadMessages[thread.threadId].map((message, idx) => (
                            <ListItem
                              key={message.messageId}
                              sx={{
                                mb: 1,
                                bgcolor: message.role === 'user' ? 'primary.50' : 'secondary.50',
                                borderRadius: 1,
                                border: 1,
                                borderColor: 'divider',
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Chip 
                                      label={message.role} 
                                      size="small" 
                                      color={getRoleColor(message.role)}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                      {formatDate(message.createdAt)}
                                    </Typography>
                                  </Box>
                                }
                                secondary={
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {message.content?.[0]?.text || '[No text content]'}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                          Loading messages...
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </React.Fragment>
                );
              })}
            </List>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

